import React, { useEffect, useState } from 'react';

import { Modal } from '@/components';
import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';

import { parseWarehouseError, warehouseApi } from '../api/warehouse.api';
import { norm } from '../format';
import type {
  CreateVendorPayload,
  FieldError,
  UpdateVendorPayload,
  Vendor,
  VendorStatus,
} from '../types';
import { EnumSelect, FormField, TextArea, TextInput } from './form';

/**
 * Create / edit a vendor (global master): ID Vendor (typed, or generated when
 * left blank), Vendor Name, Vendor Address, Vendor Phone — the columns of
 * ops' vendor sheet — plus Status on edit. The server validates formats and
 * returns every field error at once (422 VALIDATION) — they land inline at
 * the field; a duplicate name / code (409 VENDOR_NAME_TAKEN /
 * VENDOR_CODE_TAKEN) lands on Vendor Name / ID Vendor.
 */

const FIELDS = ['code', 'name', 'address', 'phone', 'status'] as const;
type Field = (typeof FIELDS)[number];
type Draft = Record<Field, string>;

const emptyDraft = (): Draft => ({
  code: '',
  name: '',
  address: '',
  phone: '',
  status: 'ACTIVE',
});

const draftOf = (v: Vendor | null): Draft =>
  v
    ? {
        code: v.code,
        name: v.name,
        address: v.address ?? '',
        phone: v.phone ?? '',
        status: v.status,
      }
    : emptyDraft();

/** Backend field name (camel or snake) → form field; null = not a form field. */
export const vendorFieldOf = (field: string): Field | null =>
  FIELDS.find((f) => norm(f) === norm(field)) ?? null;

/** Typed ID Vendor rule (the server's): 2 or more of A–Z 0–9 . _ / - */
export const VENDOR_CODE = /^[A-Z0-9._/-]{2,}$/;

const localErrors = (d: Draft): Partial<Record<Field, string>> => {
  const out: Partial<Record<Field, string>> = {};
  if (!d.name.trim()) out.name = 'Vendor Name wajib diisi.';
  const code = d.code.trim().toUpperCase();
  if (code && !VENDOR_CODE.test(code))
    out.code = 'ID Vendor minimal 2 karakter: huruf, angka, . _ / -';
  return out;
};

const createPayload = (d: Draft): CreateVendorPayload => {
  const body: CreateVendorPayload = { name: d.name.trim() };
  const code = d.code.trim().toUpperCase();
  if (code) body.code = code;
  if (d.address.trim()) body.address = d.address.trim();
  if (d.phone.trim()) body.phone = d.phone.trim();
  return body;
};

/**
 * Only the changed fields; a cleared address / phone is sent as null. A
 * blank ID Vendor on edit keeps the current one (it can't be unset).
 */
const updatePayload = (orig: Vendor, d: Draft): UpdateVendorPayload => {
  const before = draftOf(orig);
  const body: UpdateVendorPayload = {};
  const code = d.code.trim().toUpperCase();
  if (code && code !== before.code.trim().toUpperCase()) body.code = code;
  if (d.name.trim() !== before.name.trim()) body.name = d.name.trim();
  (['address', 'phone'] as const).forEach((f) => {
    const v = d[f].trim();
    if (v !== before[f].trim()) body[f] = v || null;
  });
  if (d.status !== before.status) body.status = d.status as VendorStatus;
  return body;
};

/** Code-only 409s pinned to the field that fixes them. */
const CODE_FIELD: Record<string, Field> = {
  VENDOR_NAME_TAKEN: 'name',
  VENDOR_CODE_TAKEN: 'code',
};

const VendorFormModal: React.FC<{
  open: boolean;
  /** null = create. */
  vendor: Vendor | null;
  onClose: () => void;
  onSaved: (vendor: Vendor, mode: 'create' | 'edit') => void;
}> = ({ open, vendor, onClose, onSaved }) => {
  const [draft, setDraft] = useState<Draft>(draftOf(vendor));
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(draftOf(vendor));
    setErrors({});
    setFormErrors([]);
    setBusy(false);
  }, [open, vendor]);

  if (!open) return null;

  const editing = !!vendor;
  const set = (f: Field) => (v: string) => {
    setDraft((d) => ({ ...d, [f]: v }));
    if (errors[f]) setErrors((e) => ({ ...e, [f]: undefined }));
  };
  const before = draftOf(vendor);
  const dirty = FIELDS.some((f) => draft[f].trim() !== before[f].trim());

  const applyServerError = (err: unknown) => {
    const e = parseWarehouseError(err);
    const pinned = e.code ? CODE_FIELD[e.code] : undefined;
    if (pinned) {
      setErrors({ [pinned]: e.message });
      setFormErrors([]);
      return;
    }
    const byField: Partial<Record<Field, string>> = {};
    const rest: string[] = [];
    e.fieldErrors.forEach((fe: FieldError) => {
      const f = vendorFieldOf(fe.field);
      // Status is only on the edit form.
      if (f && (f !== 'status' || editing)) {
        if (!byField[f]) byField[f] = fe.message;
      } else rest.push(fe.message);
    });
    setErrors(byField);
    setFormErrors(
      rest.length || Object.keys(byField).length ? rest : [e.message]
    );
  };

  const submit = async () => {
    const local = localErrors(draft);
    setErrors(local);
    setFormErrors([]);
    if (Object.keys(local).length) return;
    if (vendor && !dirty) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      const saved = vendor
        ? await warehouseApi.updateVendor(
            vendor.id,
            updatePayload(vendor, draft)
          )
        : await warehouseApi.createVendor(createPayload(draft));
      onSaved(saved, vendor ? 'edit' : 'create');
    } catch (err) {
      applyServerError(err);
    } finally {
      setBusy(false);
    }
  };

  const field = (
    f: Exclude<Field, 'status' | 'address'>,
    label: string,
    opts: {
      required?: boolean;
      placeholder?: string;
      icon?: string;
      inputMode?: 'tel';
      mono?: boolean;
      hint?: string;
    } = {}
  ) => (
    <FormField
      label={label}
      htmlFor={`vendor-${f}`}
      required={opts.required}
      optional={!opts.required}
      hint={opts.hint}
      error={errors[f] ?? null}
    >
      <TextInput
        id={`vendor-${f}`}
        size="md"
        value={draft[f]}
        onChange={set(f)}
        placeholder={opts.placeholder}
        icon={opts.icon}
        inputMode={opts.inputMode}
        invalid={!!errors[f]}
        className={opts.mono ? 'font-mono' : undefined}
      />
    </FormField>
  );

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      icon="ri-store-2-line"
      title={editing ? 'Edit vendor' : 'Tambah vendor'}
      subtitle={vendor ? `${vendor.code} · ${vendor.name}` : undefined}
      width="lg"
      confirmOnClose={!busy && dirty}
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            onClick={onClose}
            disabled={busy}
          >
            Batal
          </Button>
          <Button
            onClick={() => void submit()}
            loading={busy}
            leftIcon={<i className="ri-check-line text-[14px]" />}
            data-testid="vendor-submit"
          >
            {editing ? 'Simpan' : 'Tambah vendor'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3" data-testid="vendor-form">
        {formErrors.length > 0 && (
          <Alert
            status="error"
            appearance="stroke"
            size="lg"
            title="Gagal menyimpan"
            data-testid="vendor-errors"
          >
            <ul className="list-disc pl-4">
              {formErrors.map((m, i) => (
                <li key={`${i}-${m}`}>{m}</li>
              ))}
            </ul>
          </Alert>
        )}
        {field('code', 'ID Vendor', {
          mono: true,
          placeholder: editing ? undefined : 'Otomatis jika kosong',
        })}
        {field('name', 'Vendor Name', {
          required: true,
        })}
        <FormField
          label="Vendor Address"
          htmlFor="vendor-address"
          optional
          error={errors.address ?? null}
        >
          <TextArea
            id="vendor-address"
            value={draft.address}
            onChange={set('address')}
            invalid={!!errors.address}
          />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          {field('phone', 'Vendor Phone', {
            icon: 'ri-phone-line',
            inputMode: 'tel',
            placeholder: '021… / 08…',
          })}
          {editing && (
            <FormField
              label="Status"
              htmlFor="vendor-status"
              error={errors.status ?? null}
            >
              <EnumSelect
                id="vendor-status"
                value={draft.status}
                onChange={set('status')}
                invalid={!!errors.status}
                options={[
                  { value: 'ACTIVE', label: 'Active' },
                  { value: 'INACTIVE', label: 'Inactive' },
                ]}
              />
            </FormField>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default VendorFormModal;
